import React from "react";
import {
  FeatherChevronDown,
  FeatherRefreshCw,
  FeatherSearch,
} from "@subframe/core";
import { Button } from "../../../../ui/components/Button";
import { DropdownMenu } from "../../../../ui/components/DropdownMenu";
import { TextField } from "../../../../ui/components/TextField";
import { SortDropdown } from "../../../../shared/presentation/components/SortDropdown";
import { CreditNoteDocumentType, CreditNoteListStatus } from "../../domain";
import { CreditNoteStatusFilterPills } from "./CreditNoteStatusFilterPills";

interface CreditNoteListToolbarProps {
  status: CreditNoteListStatus;
  onStatusChange: (status: CreditNoteListStatus) => void;
  documentType: CreditNoteDocumentType;
  onDocumentTypeChange: (documentType: CreditNoteDocumentType) => void;
  searchTerm: string;
  onSearchTermChange: (value: string) => void;
  sort: {
    by: string;
    order: string;
    label: string;
  };
  onSortChange: (sortBy: string, sortOrder: string, label: string) => void;
  onRefresh: () => void;
  loading: boolean;
}

export const CreditNoteListToolbar: React.FC<CreditNoteListToolbarProps> = ({
  status,
  onStatusChange,
  documentType,
  onDocumentTypeChange,
  searchTerm,
  onSearchTermChange,
  sort,
  onSortChange,
  onRefresh,
  loading,
}) => {
  return (
    <div className="flex w-full items-center justify-between gap-4">
      <CreditNoteStatusFilterPills value={status} onChange={onStatusChange} />

      <div className="flex items-center gap-4">
        <TextField
          className="h-auto w-80 flex-none"
          variant="filled"
          label=""
          helpText=""
          icon={<FeatherSearch />}
        >
          <TextField.Input
            placeholder="Search credit notes..."
            value={searchTerm}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              onSearchTermChange(event.target.value)
            }
          />
        </TextField>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <Button
              variant="neutral-secondary"
              iconRight={<FeatherChevronDown />}
              disabled={loading}
            >
              {getDocumentTypeLabel(documentType)}
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content align="end">
              <DropdownMenu>
                {(["all", "invoice", "bill"] as CreditNoteDocumentType[]).map(
                  (value) => (
                    <DropdownMenu.DropdownItem
                      key={value}
                      onClick={() => onDocumentTypeChange(value)}
                    >
                      {getDocumentTypeLabel(value)}
                    </DropdownMenu.DropdownItem>
                  ),
                )}
              </DropdownMenu>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <SortDropdown
          currentSort={sort}
          onSortChange={onSortChange}
          disabled={loading}
        />
        <Button
          variant="neutral-tertiary"
          icon={<FeatherRefreshCw />}
          onClick={onRefresh}
          disabled={loading}
        >
          Refresh
        </Button>
      </div>
    </div>
  );
};

const getDocumentTypeLabel = (documentType: CreditNoteDocumentType) => {
  switch (documentType) {
    case "invoice":
      return "Invoice Credits";
    case "bill":
      return "Bill Credits";
    case "all":
    default:
      return "All Types";
  }
};
